import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Activity, TrendingUp, History, Loader2, AlertTriangle } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { useUzkaObrazovka } from "../lib/obrazovka.js";

// Měření InBody v ADMIN detailu klienta (3.8.2026) — sekce Výživa.
// Stejný přehled, jaký má klientka v klientské aplikaci (tab Měření):
//   1. Poslední měření — 6 metrik s deltou vs. PRVNÍ měření (zelená = zlepšení)
//   2. Vývoj v čase — line graf s přepínačem metrik
//   3. Historie měření — tabulka všech měření (nejnovější nahoře) s deltou vs. předchozí
// Všechny tři části jsou sbalené dropdowny — otevírají se až kliknutím.
// Data: /api/klient-inbody (PG inbody_measurements, import z InBody 270).
// Vidí: owner, manager, CSM, nutritionist (endpoint má role guard; trenér
// kartu nemá — VyzivaExpandable ji skrývá přes jenMasterplan).

// positive: true = vyšší hodnota je zlepšení (svaly), false = nižší je lepší
// (váha/tuk), null = neutrální (voda). 1:1 s klientskou appkou.
const METRICS = [
  { key: "weight", field: "weight_kg",   label: "Váha",            short: "Váha",      unit: "kg", color: "#7c5db0", positive: false },
  { key: "smm",    field: "smm_kg",      label: "Svaly (SMM)",     short: "Svaly",     unit: "kg", color: "#2196F3", positive: true },
  { key: "ffm",    field: "ffm_kg",      label: "Beztuková hmota", short: "Beztuková", unit: "kg", color: "#9C27B0", positive: true },
  { key: "bfm",    field: "bfm_kg",      label: "Tělesný tuk",     short: "Tuk",       unit: "kg", color: "#FF9800", positive: false },
  { key: "pbf",    field: "pbf_percent", label: "Tuk %",           short: "Tuk %",     unit: "%",  color: "#f44336", positive: false },
  { key: "tbw",    field: "tbw_kg",      label: "Voda (TBW)",      short: "Voda",      unit: "l",  color: "#00BCD4", positive: null },
  { key: "bmr",    field: "bmr_kcal",    label: "Bazální metab.",  short: "BMR",       unit: "kcal", color: "#8d6e63", positive: null, dec: 0 },
];

const fmtCz = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "—" : `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
};
const fmt1 = (v, dec = 1) => (v == null ? "—" : Number(v).toFixed(dec).replace(".", ","));

// Trend delty dle positive flagu: "good" | "bad" | "neutral".
function trendOf(delta, positive) {
  if (delta == null || Math.abs(delta) < 0.05 || positive == null) return "neutral";
  const rising = delta > 0;
  return positive === rising ? "good" : "bad";
}

// Sbalený dropdown jedné části (default zavřeno).
function DropCast({ icon: Icon, title, badge, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-[48px] w-full items-center gap-2 bg-slate-50/70 px-3 py-2 text-left transition-colors hover:bg-slate-100"
      >
        <Icon className="w-4 h-4 text-brand-500 shrink-0" />
        <span className="text-sm font-semibold text-slate-700 flex-1">{title}</span>
        {badge && <span className="text-[11px] text-slate-400 tabular-nums">{badge}</span>}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="p-3">{children}</div>}
    </div>
  );
}

// Delta badge (▲/▼ + hodnota, barva dle trendu).
function DeltaBadge({ delta, unit, positive, dec = 1 }) {
  if (delta == null) return null;
  if (Math.abs(delta) < 0.05) return <span className="text-[11px] text-slate-400">beze změny</span>;
  const trend = trendOf(delta, positive);
  const cls = trend === "good" ? "bg-emerald-50 text-emerald-700" : trend === "bad" ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-1.5 py-0.5 rounded-lg tabular-nums ${cls}`}>
      {delta > 0 ? "▲" : "▼"} {delta > 0 ? "+" : "−"}{Math.abs(delta).toFixed(dec).replace(".", ",")} {unit}
    </span>
  );
}

// SVG line graf (adaptace grafu z klientské appky na světlé admin téma).
function LineChart({ points, color, unit, dec = 1, kompakt = false }) {
  const [tip, setTip] = useState(null);
  // 9.9.2026 — na telefonu se kreslí do menšího plátna (360 místo 560 jednotek).
  // SVG se roztáhne na šířku, takže na širokém plátně se popisky os zmenší tak,
  // že se nedaly přečíst. Na menším plátně vyjdou zhruba 1:1.
  const W = kompakt ? 360 : 560, H = kompakt ? 190 : 200;
  const pL = kompakt ? 40 : 42, pB = kompakt ? 24 : 26, pT = kompakt ? 12 : 14, pR = kompakt ? 12 : 14;
  const fs = kompakt ? 12 : 11;
  const iW = W - pL - pR, iH = H - pB - pT;
  if (!points.length) return <div className="text-sm text-slate-400 italic text-center py-6">Žádná data</div>;
  const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  let yMin = Math.min(...ys), yMax = Math.max(...ys);
  if (yMin === yMax) { yMin -= 1; yMax += 1; }
  const span = yMax - yMin; yMin -= span * 0.15; yMax += span * 0.15;
  const xSpan = Math.max(1, xMax - xMin);
  const xPx = (t) => pL + ((t - xMin) / xSpan) * iW;
  const yPx = (v) => pT + iH - ((v - yMin) / (yMax - yMin)) * iH;
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${xPx(p.x)} ${yPx(p.y)}`).join(" ");
  const yTicks = [yMax - (yMax - yMin) * 0.15, (yMin + yMax) / 2, yMin + (yMax - yMin) * 0.15];
  const xFmt = (t) => { const d = new Date(t); return `${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)}`; };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", overflow: "visible" }}>
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={pL} x2={W - pR} y1={yPx(v)} y2={yPx(v)} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3,3" />
          <text x={pL - 6} y={yPx(v) + 4} fill="#94a3b8" fontSize={fs} fontWeight="600" textAnchor="end">{v.toFixed(dec).replace(".", ",")}</text>
        </g>
      ))}
      <text x={pL} y={H - 6} fill="#94a3b8" fontSize={fs} fontWeight="600" textAnchor="start">{xFmt(xMin)}</text>
      {points.length > 1 && <text x={W - pR} y={H - 6} fill="#94a3b8" fontSize={fs} fontWeight="600" textAnchor="end">{xFmt(xMax)}</text>}
      {points.length > 1 && (
        <path d={`${path} L ${xPx(xMax)} ${pT + iH} L ${xPx(xMin)} ${pT + iH} Z`} fill={color} opacity="0.10" />
      )}
      {points.length > 1 && <path d={path} stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />}
      {points.map((p, i) => (
        <g key={i}
          onMouseEnter={() => setTip({ x: xPx(p.x), y: yPx(p.y), v: p.y, d: p.x })}
          onMouseLeave={() => setTip(null)}
          onClick={() => setTip({ x: xPx(p.x), y: yPx(p.y), v: p.y, d: p.x })}>
          <circle cx={xPx(p.x)} cy={yPx(p.y)} r="14" fill="transparent" />
          <circle cx={xPx(p.x)} cy={yPx(p.y)} r="4.5" fill={color} stroke="#fff" strokeWidth="2" />
        </g>
      ))}
      {tip && (() => {
        const txt = `${tip.v.toFixed(dec).replace(".", ",")} ${unit}, ${fmtCz(new Date(tip.d).toISOString())}`;
        const w = Math.max(70, txt.length * (kompakt ? 7 : 6.4) + 14);
        const tx = Math.min(Math.max(tip.x - w / 2, pL), W - pR - w);
        const ty = Math.max(pT + 10, tip.y - 30);
        return (
          <g>
            <rect x={tx} y={ty - 19} width={w} height="24" rx="6" fill="#1e293b" />
            <text x={tx + w / 2} y={ty - 3} fill="#fff" fontSize={kompakt ? 12.5 : 11.5} textAnchor="middle" fontWeight="700">{txt}</text>
          </g>
        );
      })()}
    </svg>
  );
}

export default function InBodySection({ klientId, klientNotion, onCount }) {
  // POZOR: klientNotion je URL na Notion stránku, ne ID — měření jsou klíčovaná
  // na clients.notion_id (= prop klientId, předává ho VyzivaExpandable).
  const id = klientId || klientNotion;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [metric, setMetric] = useState("weight");
  // GDPR 19.10.2026: false = klientka zatím nedala souhlas se zdravotními údaji.
  const [souhlas, setSouhlas] = useState(null);
  // 9.9.2026 — na telefonu jsou hodnoty řádky a historie karty, ať se nic neroluje do strany.
  const uzka = useUzkaObrazovka();

  useEffect(() => {
    let zij = true;
    (async () => {
      try {
        const r = await apiFetch(`/api/klient-inbody?klientId=${encodeURIComponent(id)}`);
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Načtení měření selhalo");
        if (zij) { setData(j.measurements || []); onCount?.(j.count || 0); setSouhlas(j.souhlas === false ? false : j.souhlas === true ? true : null); }
      } catch (e) { if (zij) setErr(e.message); }
      finally { if (zij) setLoading(false); }
    })();
    return () => { zij = false; };
  }, [id]);

  const meas = data || [];
  const latest = meas[meas.length - 1] || null;
  const first = meas[0] || null;
  const activeMetric = METRICS.find((m) => m.key === metric) || METRICS[0];
  const chartPoints = useMemo(
    () => meas.filter((m) => m[activeMetric.field] != null).map((m) => ({ x: new Date(m.measured_at).getTime(), y: Number(m[activeMetric.field]) })),
    [meas, activeMetric.field]
  );
  const delta = (field) => {
    if (!latest || !first || meas.length < 2) return null;
    if (latest[field] == null || first[field] == null) return null;
    return latest[field] - first[field];
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-slate-400 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám měření…</div>;
  if (err) return <div className="text-sm text-rose-600 py-1">{err}</div>;
  const upozorneniSouhlas = souhlas === false && (
    <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 px-3 py-2.5 text-[13px] leading-snug text-amber-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>Klientka zatím nedala souhlas se zdravotními údaji. V aplikaci se jí ukáže, až otevře Jídelníček nebo Měření.</span>
    </div>
  );
  if (!meas.length) return (
    <div className="space-y-2">
      {upozorneniSouhlas}
      <div className="text-sm text-slate-400 italic py-1">Zatím žádné měření InBody. Data se importují automaticky ze záloh InBody 270.</div>
    </div>
  );

  return (
    <div className="space-y-2">
      {upozorneniSouhlas}
      {/* 1. Poslední měření — pilly s deltou vs. první měření */}
      <DropCast icon={Activity} title="Poslední měření" badge={fmtCz(latest.measured_at)}>
        {meas.length >= 2 && (
          <div className="text-[11px] text-slate-400 mb-2">Změna (▲/▼) je vůči prvnímu měření {fmtCz(first.measured_at)}, zelená = zlepšení</div>
        )}
        {/* 9.9.2026 — telefon: název vlevo, velké číslo s jednotkou a změna jako pilulka.
            Do dlaždic se dlouhé názvy jako „Beztuková hmota" nevešly. */}
        {uzka && (
          <div className="space-y-1.5">
            {METRICS.map((m) => {
              const v = latest[m.field];
              const d = delta(m.field);
              return (
                <div key={m.key} className="flex min-h-[52px] items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2">
                  <span className="min-w-0 flex-1 text-[13.5px] font-semibold text-slate-700">{m.label}</span>
                  <span className="shrink-0 text-[18px] font-extrabold tabular-nums text-slate-900">
                    {fmt1(v, m.dec ?? 1)}<span className="ml-0.5 text-[12px] font-bold text-slate-400">{m.unit}</span>
                  </span>
                  <span className="flex min-w-[54px] shrink-0 justify-end">
                    <DeltaBadge delta={d} unit={m.unit} positive={m.positive} dec={m.dec ?? 1} />
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <div className={`${uzka ? "hidden" : "grid"} grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2`}>
          {METRICS.map((m) => {
            const v = latest[m.field];
            const d = delta(m.field);
            const trend = trendOf(d, m.positive);
            const ring = trend === "good" ? "border-emerald-200 bg-emerald-50/40" : trend === "bad" ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white";
            return (
              <div key={m.key} className={`rounded-xl border px-2 py-2.5 text-center ${ring}`}>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 truncate">{m.label}</div>
                <div className="text-lg font-bold text-slate-800 tabular-nums leading-tight mt-0.5">{fmt1(v, m.dec ?? 1)}</div>
                <div className="text-[11px] text-slate-400">{m.unit}</div>
                <div className="mt-1 min-h-[18px]"><DeltaBadge delta={d} unit={m.unit} positive={m.positive} dec={m.dec ?? 1} /></div>
              </div>
            );
          })}
        </div>
      </DropCast>

      {/* 2. Vývoj v čase — graf s přepínačem metrik */}
      <DropCast icon={TrendingUp} title="Vývoj v čase" badge={`${meas.length}× měřeno`}>
        {/* 9.9.2026 — telefon: místo sedmi pilulek přes tři řádky jeden výběr přes celou šířku. */}
        {uzka ? (
          <label className="mb-3 flex min-h-[44px] items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-3">
            <span className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-slate-400">Metrika</span>
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              className="ml-auto min-w-0 flex-1 bg-transparent text-right text-[14px] font-extrabold text-slate-800 focus:outline-none"
            >
              {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label} ({m.unit})</option>)}
            </select>
          </label>
        ) : (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {METRICS.map((m) => (
              <button key={m.key} type="button" onClick={() => setMetric(m.key)}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                  metric === m.key ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
                style={metric === m.key ? { background: m.color } : undefined}>
                {m.short}
              </button>
            ))}
          </div>
        )}
        <LineChart points={chartPoints} color={activeMetric.color} unit={activeMetric.unit} dec={activeMetric.dec ?? 1} kompakt={uzka} />
        {uzka && <div className="mt-1 text-[11.5px] text-slate-400">Klepnutím na bod se ukáže hodnota a datum.</div>}
      </DropCast>

      {/* 3. Historie — tabulka všech měření, nejnovější nahoře, delta vs. předchozí */}
      <DropCast icon={History} title="Historie měření" badge={`${meas.length} záznamů`}>
        {/* 9.9.2026 — telefon: každé měření je karta se šesti dlaždicemi místo tabulky
            široké 600 px, která na telefonu končila mimo obrazovku. */}
        {uzka && (
          <div className="space-y-2">
            {[...meas].reverse().map((m, i, arr) => {
              const prev = arr[i + 1];
              return (
                <div key={m.measured_at} className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] font-bold tabular-nums text-slate-800">{fmtCz(m.measured_at)}</span>
                    {i === 0 && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11.5px] font-bold text-emerald-700">nejnovější</span>}
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-1.5">
                    {METRICS.map((mm) => {
                      const v = m[mm.field];
                      const d = prev && v != null && prev[mm.field] != null ? v - prev[mm.field] : null;
                      const trend = trendOf(d, mm.positive);
                      const dCls = trend === "good" ? "text-emerald-600" : trend === "bad" ? "text-rose-500" : "text-slate-300";
                      return (
                        <div key={mm.key} className="rounded-xl bg-slate-50 px-1 py-1.5 text-center">
                          <div className="truncate text-[11px] font-bold uppercase tracking-wide text-slate-400">{mm.short}</div>
                          <div className="text-[16px] font-extrabold leading-tight tabular-nums text-slate-800">{fmt1(v, mm.dec ?? 1)}</div>
                          <div className={`text-[11px] font-bold tabular-nums ${dCls}`}>
                            {d != null && Math.abs(d) >= 0.05 ? `${d > 0 ? "+" : "−"}${Math.abs(d).toFixed(mm.dec ?? 1).replace(".", ",")}` : "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="text-[11.5px] leading-snug text-slate-400">Změna je vůči předchozímu měření, zelená je zlepšení.</div>
          </div>
        )}
        <div className={`${uzka ? "hidden" : ""} overflow-x-auto -mx-1`}>
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="text-left font-semibold py-1.5 px-1">Datum</th>
                {METRICS.map((m) => <th key={m.key} className="text-right font-semibold py-1.5 px-1">{m.short}</th>)}
              </tr>
            </thead>
            <tbody>
              {[...meas].reverse().map((m, i, arr) => {
                const prev = arr[i + 1]; // starší měření (reverse → další v poli)
                return (
                  <tr key={m.measured_at} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-1.5 px-1 font-medium text-slate-700 tabular-nums whitespace-nowrap">{fmtCz(m.measured_at)}</td>
                    {METRICS.map((mm) => {
                      const v = m[mm.field];
                      const d = prev && v != null && prev[mm.field] != null ? v - prev[mm.field] : null;
                      const trend = trendOf(d, mm.positive);
                      const dCls = trend === "good" ? "text-emerald-600" : trend === "bad" ? "text-rose-500" : "text-slate-300";
                      return (
                        <td key={mm.key} className="py-1.5 px-1 text-right tabular-nums text-slate-700">
                          {fmt1(v, mm.dec ?? 1)}
                          {d != null && Math.abs(d) >= 0.05 && (
                            <span className={`block text-[11px] font-semibold ${dCls}`}>{d > 0 ? "+" : "−"}{Math.abs(d).toFixed(mm.dec ?? 1).replace(".", ",")}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DropCast>
    </div>
  );
}
